import { useState, useRef, useEffect, useCallback } from "react";
import { useLang, type Lang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";
import { getPathWithRef } from "../../utils/attribution";

interface Props {
  onHelp: () => void;
  hideHelp?: boolean;
  onLogoClick?: () => void;
}

const LANG_OPTIONS: { value: Lang; label: string; native: string }[] = [
  { value: "es", label: "Español", native: "ES" },
  { value: "ar", label: "العربية", native: "AR" },
  { value: "zh", label: "中文", native: "中文" },
  { value: "fr", label: "Français", native: "FR" },
  { value: "en", label: "English", native: "EN" },
];

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/* ─────────────────────────── DeleteModal ─────────────────────────── */
interface DeleteModalProps {
  onClose: () => void;
  t: (k: string) => string;
}

function DeleteModal({ onClose, t }: DeleteModalProps) {
  const [countdown, setCountdown] = useState(3);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = setInterval(() => {
      setCountdown((c) => c - 1);
    }, 1000);
    return () => clearInterval(id);
  }, [countdown]);

  // Trap focus / close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleConfirm = () => {
    setConfirmed(true);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.50)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        style={{
          animation: "slideUp 0.22s cubic-bezier(0.22,1,0.36,1) both",
        }}
      >
        {confirmed ? (
          /* ── Success state ── */
          <div className="flex flex-col items-center px-8 py-10 gap-4 text-center">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
              <i className="ri-check-line text-white text-xl" />
            </div>
            <p className="text-lg font-bold text-slate-900">Solicitud recibida</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Te enviaremos una confirmación por correo electrónico.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full py-2.5 rounded-xl bg-slate-600 text-white text-sm font-semibold hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("header.close")}
            </button>
          </div>
        ) : (
          /* ── Confirmation state ── */
          <>
            <div className="px-6 pt-7 pb-5">
              <p className="text-lg font-bold text-slate-900 mb-4">{t("header.deleteTitle")}</p>
              <div className="flex flex-col gap-3">
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("header.deleteBody1")}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("header.deleteBody2")}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("header.deleteBody3").split("hola@globalmobility.law")[0]}
                  <a
                    href="mailto:hola@globalmobility.law"
                    className="text-slate-900 font-semibold underline decoration-dotted"
                  >
                    hola@globalmobility.law
                  </a>
                  {t("header.deleteBody3").split("hola@globalmobility.law")[1]}
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 flex flex-col gap-2.5">
              <button
                type="button"
                disabled={countdown > 0}
                onClick={handleConfirm}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer whitespace-nowrap ${
                  countdown > 0
                    ? "bg-red-500 opacity-50 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 opacity-100"
                }`}
              >
                {countdown > 0
                  ? t("header.deleteCountdown").replace("{{n}}", String(countdown))
                  : t("header.deleteConfirm")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("header.cancel")}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────── Header ─────────────────────────── */
export default function Header({ onHelp, hideHelp = false, onLogoClick }: Props) {
  const { lang, setLang, t, isRTL } = useLang();
  const { saveState, data } = useForm();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    function handleLangOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    if (langOpen) document.addEventListener("mousedown", handleLangOutside);
    return () => document.removeEventListener("mousedown", handleLangOutside);
  }, [langOpen]);

  const openDeleteModal = useCallback(() => {
    setDropdownOpen(false);
    setLangOpen(false);
    setDeleteModalOpen(true);
  }, []);

  const handleLogout = useCallback(() => {
    setDropdownOpen(false);
    setLangOpen(false);
    // RefGuard requires ?ref= (or re-validated session). Resolve login URL before clearing storage
    // so partner token from the original funnel is preserved in the query string.
    const loginPath = getPathWithRef("/login");
    try {
      localStorage.clear();
    } catch {
      // ignore storage access issues
    }
    try {
      sessionStorage.clear();
    } catch {
      // ignore storage access issues
    }
    window.location.assign(loginPath);
  }, []);

  const initials = getInitials(data.fullName);

  return (
    <>
      <header
        className="w-full sticky top-0 z-40 bg-white"
        style={{
          borderTop: "2px solid #a81c1c",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div className="max-w-[780px] mx-auto px-5 h-14 flex items-center justify-between gap-4">

          {/* Left — Logo image + Firm name */}
          <button
            type="button"
            onClick={onLogoClick}
            className="flex items-center min-w-0 cursor-pointer hover:opacity-80 transition-opacity h-full py-1"
          >
            <img
              src="/header-logo.png"
              alt="Global Mobility"
              className="h-10 w-auto object-contain"
              style={{ maxHeight: "40px" }}
            />
          </button>

          {/* Right — auto-save + language + profile dropdown */}
          <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>

            {/* Auto-save indicator */}
            {saveState !== "idle" && (
              <span
                className={`text-xs font-medium transition-all whitespace-nowrap ${
                  saveState === "saved" ? "text-emerald-600" : "text-slate-400"
                }`}
              >
                {saveState === "saving" ? (
                  <span className="flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin text-xs" />
                    {t("header.saving")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <i className="ri-checkbox-circle-line text-xs" />
                    {t("header.saved")}
                  </span>
                )}
              </span>
            )}

            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => { setLangOpen((v) => !v); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-translate-2 text-slate-400 text-[13px]" />
                <span className="text-[12px] font-bold text-slate-600 uppercase tracking-wide">
                  {LANG_OPTIONS.find((o) => o.value === lang)?.native}
                </span>
                <i className={`ri-arrow-down-s-line text-slate-400 text-xs transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>

              {langOpen && (
                <div
                  className={`absolute top-full mt-2 bg-white border border-slate-100 rounded-xl overflow-hidden z-50 min-w-[150px] ${isRTL ? "left-0" : "right-0"}`}
                  style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
                >
                  {LANG_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setLang(opt.value); setLangOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        lang === opt.value
                          ? "bg-slate-50 text-slate-900 font-semibold"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      } ${isRTL ? "flex-row-reverse text-right" : "text-left"}`}
                    >
                      {lang === opt.value
                        ? <i className="ri-check-line text-slate-500 text-xs shrink-0" />
                        : <span className="w-3.5 shrink-0" />
                      }
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile dropdown — mobile: initials only; sm+: name + chevron */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => { setLangOpen(false); setDropdownOpen((v) => !v); }}
                className={`flex items-center gap-1 sm:gap-2 min-w-0 cursor-pointer group rounded-lg sm:rounded-none p-0.5 sm:p-0 -mr-0.5 sm:mr-0 ${isRTL ? "flex-row-reverse" : ""}`}
                aria-expanded={dropdownOpen}
                aria-haspopup="menu"
                aria-label={data.fullName.trim() ? `Account, ${data.fullName}` : "Account menu"}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
                  style={{ background: "#334155" }}
                >
                  {initials}
                </div>
                <span className="hidden sm:block text-[13px] font-medium text-slate-700 truncate max-w-[100px] sm:max-w-[160px] group-hover:text-slate-900 transition-colors">
                  {data.fullName}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-slate-400 text-sm shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </button>

              {dropdownOpen && (
                <div
                  className={`absolute top-full mt-2 bg-white border border-slate-100 rounded-xl overflow-hidden z-50 min-w-[220px] ${isRTL ? "left-0" : "right-0"}`}
                  style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
                >
                  {/* User name */}
                  <div className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{data.fullName}</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-100 mx-0" />

                  {/* Help row */}
                  <button
                    onClick={() => { onHelp(); setDropdownOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap ${isRTL ? "flex-row-reverse text-right" : "text-left"}`}
                  >
                    <i className="ri-question-line text-slate-400 text-sm shrink-0" />
                    <span>{t("header.help")}</span>
                  </button>

                  {/* Privacy policy row */}
                  <a
                    href="https://globalmobility.law/politica-privacidad"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap ${isRTL ? "flex-row-reverse text-right" : "text-left"}`}
                  >
                    <i className="ri-shield-check-line text-slate-400 text-sm shrink-0" />
                    <span>{t("header.privacy")}</span>
                  </a>

                  {/* Logout + delete account */}
                  <div className="h-px bg-slate-100" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap ${isRTL ? "flex-row-reverse text-right" : "text-left"}`}
                  >
                    <i className="ri-logout-box-r-line text-slate-400 text-sm shrink-0" />
                    <span>{t("header.logout")}</span>
                  </button>
                  <div className="h-px bg-slate-100" />
                  <div className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={openDeleteModal}
                      className={`flex items-center gap-1.5 text-sm text-red-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap ${isRTL ? "flex-row-reverse" : ""}`}
                    >
                      <i className="ri-delete-bin-line text-red-400 text-base" />
                      {t("header.deleteAccount")}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </header>

      {/* Delete account modal */}
      {deleteModalOpen && (
        <DeleteModal onClose={() => setDeleteModalOpen(false)} t={t} />
      )}
    </>
  );
}
